import { useState } from 'react'
import { Plus, Trash2, CheckCircle2, XCircle, Clock, Send, Loader2, Info, Eye, Wand2, ShieldCheck, CalendarClock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { toast } from 'sonner'
import { api } from '@/api/client'
import { useTemplates, useCreateTemplate, useDeleteTemplate } from '@/api/hooks/useBroadcast'

interface Account { igUserId: string; pageId: string; name: string }

// Cascas neutras (o que a Meta revisa/aprova como UTILITY). O conteúdo real
// entra nas variáveis {{1}}/{{2}} no envio — a Meta não vê isso na revisão.
// Cascas NEUTRAS que a Meta aprova como UTILITY (proven: 151/151).
// REGRA: nunca usar "acessar/clique/aproveite/toque para acessar" nem link de domínio
// cru — isso dispara TAG_SHOULD_BE_MARKETING e a Meta rejeita. Use "atualização" +
// "conferir" e link t.co. O texto apelativo vai no {{1}} (var1), que a Meta não revisa.
const SHELLS = [
  'Atualização na sua conta. {{1}} Toque no botão abaixo para conferir.',
  'Há uma atualização na sua conta. {{1}} Toque no botão abaixo para conferir.',
  'Registramos uma atualização na sua conta. {{1}} Toque no botão abaixo para conferir.',
]

// Preenche {{1}}/{{2}} numa casca com os valores dados (pra preview)
function fill(shell: string, v1: string, v2: string): string {
  return shell
    .replace(/\{\{\s*1\s*\}\}/g, v1)
    .replace(/\{\{\s*2\s*\}\}/g, v2)
    .replace(/\{\{\s*first_name\s*\}\}/g, 'Maria')
    .replace(/\s+/g, ' ')
    .trim()
}

// Botão do template — schema completo espelhando o modelo importável do Chatpião.
// type=url leva pra fora; type=postback vira QUICK_REPLY (não sai do chat) e carrega
// a resposta acoplada (reply_*), entregue quando a pessoa clica.
type TplButton = {
  type: 'url' | 'postback'
  text: string
  url: string
  payload: string
  reply_text: string
  reply_image: string
  reply_link: string
  reply_btn_text: string
  reply_payload: string
}
const emptyButton = (over: Partial<TplButton> = {}): TplButton => ({
  type: 'url', text: '', url: '', payload: '',
  reply_text: '', reply_image: '', reply_link: '', reply_btn_text: '', reply_payload: '',
  ...over,
})

export function TemplatesTab({ accounts, t, onSchedule }: { accounts: Account[]; t: (s: string) => string; onSchedule?: (templateName: string) => void }) {
  const [name, setName] = useState('')
  const [shell, setShell] = useState(SHELLS[0])
  const [var1, setVar1] = useState('OI VIDA 😍 senti sua falta! Clique abaixo pra ver meu WhatsApp 👇')
  const [var2, setVar2] = useState('')
  const [example, setExample] = useState('detalhes')
  const [example2, setExample2] = useState('novidades')
  const [buttons, setButtons] = useState<TplButton[]>([emptyButton({ text: 'ACESSAR' })])
  const [imageUrl, setImageUrl] = useState('')
  const [headerTitle, setHeaderTitle] = useState('Novidade pra você')
  const [uploadingImg, setUploadingImg] = useState(false)
  const [creating, setCreating] = useState(false)
  const [firing, setFiring] = useState<string | null>(null)

  const { data: templates = [], isLoading } = useTemplates()
  const createTpl = useCreateTemplate()
  const deleteTpl = useDeleteTemplate()

  const setBtn = (i: number, patch: Partial<TplButton>) =>
    setButtons(bs => bs.map((b, j) => (j === i ? { ...b, ...patch } : b)))
  const addBtn = () => setButtons(bs => (bs.length < 3 ? [...bs, emptyButton()] : bs))
  const removeBtn = (i: number) => setButtons(bs => (bs.length > 1 ? bs.filter((_, j) => j !== i) : bs))
  // Atalho do Chatpião: todos os botões URL apontam pro mesmo link do 1º
  const sameLinkAll = () => setButtons(bs => bs.map(b => (b.type === 'url' ? { ...b, url: bs[0].url } : b)))

  const uses1 = /\{\{\s*1\s*\}\}/.test(shell)
  const uses2 = /\{\{\s*2\s*\}\}/.test(shell)

  // O que a Meta REVISA (casca + exemplos neutros)
  const metaSees = fill(shell, example, example2)
  // O que o LEAD RECEBE de verdade (casca + conteúdo real)
  const leadGets = fill(shell, var1, var2)

  async function handleCreate() {
    if (!name.trim()) { toast.error(t('Dê um nome ao template')); return }
    if (!shell.trim()) { toast.error(t('A máscara não pode ficar vazia')); return }
    if (uses1 && !var1.trim()) { toast.error(t('Preencha o conteúdo da variável 1')); return }
    setCreating(true)
    try {
      const r = await createTpl.mutateAsync({
        name, body: shell,
        var1: uses1 ? var1 : undefined,
        var2: uses2 ? var2 : undefined,
        example, example2,
        buttons: buttons.filter(b => b.text.trim()),
        imageUrl: imageUrl || undefined,
        headerTitle: imageUrl ? headerTitle : undefined,
      })
      toast.success(`"${r.name}": ${r.approved} aprovadas / ${r.rejected} rejeitadas de ${r.total} páginas`)
      if (r.rejected > 0 && r.reasons && Object.keys(r.reasons).length) {
        const reasons = Object.entries(r.reasons).map(([k, v]) => `${k} (${v})`).join(', ')
        toast.warning(`${t('Motivos de rejeição')}: ${reasons}`, { duration: 8000 })
      }
      setName('')
    } catch (e: any) {
      toast.error(e?.message || t('Erro ao criar template'))
    } finally { setCreating(false) }
  }

  async function handleDelete(tplName: string) {
    if (!confirm(`${t('Apagar o template')} "${tplName}" ${t('de todas as páginas?')}`)) return
    try {
      await deleteTpl.mutateAsync(tplName)
      toast.success(t('Template apagado'))
    } catch (e: any) { toast.error(e?.message || 'Erro') }
  }

  async function handleFire(tplName: string) {
    if (!accounts.length) { toast.error(t('Nenhuma conta selecionada')); return }
    const todos = confirm(
      `Disparar "${tplName}" em ${accounts.length} páginas.\n\n` +
      `OK = TODOS os contatos (tenta a base inteira — pode pegar leads vivos que o filtro de 7 dias esconde, mas mais falham fora da janela).\n\n` +
      `Cancelar = só os de 7 dias (mais entrega, menos esforço).`
    )
    setFiring(tplName)
    try {
      const igUserIds = accounts.map(a => a.igUserId)
      const payload: any = { igUserIds, templateName: tplName, filter: todos ? 'all' : '7d' }
      if (todos) payload.r = 1
      const res: any = await api.post('/broadcast/send', payload)
      toast.success(`${t('Disparo iniciado')}: ${res.totalContacts ?? 0} ${t('contatos')} (${todos ? 'todos' : '7 dias'})`)
    } catch (e: any) {
      toast.error(e?.message || t('Erro ao disparar'))
    } finally { setFiring(null) }
  }

  return (
    <div className="space-y-6">
      {/* Info banner */}
      <div className="flex items-start gap-2 rounded-lg border border-primary/30 bg-primary/5 p-3 text-xs text-muted-foreground">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        <div>
          {t('A máscara (Passo 2) é o que a Meta revisa — mantenha-a neutra pra aprovar como utilidade. O conteúdo real que o lead recebe fica nas variáveis')}
          {' '}<code className="rounded bg-muted px-1">{'{{1}}'}</code> / <code className="rounded bg-muted px-1">{'{{2}}'}</code>{t(', preenchidas só no envio. Use')} <code className="rounded bg-muted px-1">{'{{first_name}}'}</code> {t('dentro da variável pra personalizar com o nome.')}
        </div>
      </div>

      {/* Create form */}
      <div className="rounded-xl border bg-card p-4 space-y-4">
        <div className="flex items-center gap-2 text-sm font-semibold"><Plus className="h-4 w-4" /> {t('Criar template')}</div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">{t('Nome')}</Label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="aviso_conta" className="mt-1" />
            <p className="mt-1 text-[10px] text-muted-foreground">{t('Só letras minúsculas, números e _')}</p>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">{t('Categoria')}</Label>
            <Input value="UTILITY" disabled className="mt-1 opacity-70" />
          </div>
        </div>

        {/* PASSO 1 — Conteúdo real que o lead vê */}
        <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 space-y-3">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-emerald-600 dark:text-emerald-400">
            <Eye className="h-3.5 w-3.5" /> {t('Passo 1 · Conteúdo real que o lead vê')}
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">{'{{1}}'} — {t('mensagem principal')}</Label>
            <textarea
              value={var1} onChange={e => setVar1(e.target.value)} rows={2}
              className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm"
              placeholder={t('Oi {{first_name}}, senti sua falta! Clique abaixo 👇')}
            />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">{'{{2}}'} — {t('texto extra (opcional)')}</Label>
            <Input value={var2} onChange={e => setVar2(e.target.value)} className="mt-1" placeholder={t('só usado se a máscara tiver {{2}}')} />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">{t('Botões')} ({buttons.length}/3)</Label>
              <div className="flex items-center gap-3">
                {buttons.filter(b => b.type === 'url').length > 1 && (
                  <button type="button" onClick={sameLinkAll} className="text-[10px] text-primary hover:underline">
                    🔗 {t('mesmo link em todos')}
                  </button>
                )}
                {buttons.length < 3 && (
                  <button type="button" onClick={addBtn} className="flex items-center gap-1 text-[11px] font-medium text-primary hover:underline">
                    <Plus className="h-3 w-3" /> {t('Adicionar botão')}
                  </button>
                )}
              </div>
            </div>
            {buttons.map((b, i) => (
              <div key={i} className="rounded-md border bg-background/50 p-2.5 space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-semibold text-muted-foreground">#{i + 1}</span>
                  <div className="flex gap-1">
                    <button type="button" onClick={() => setBtn(i, { type: 'url' })}
                      className={`rounded border px-2 py-0.5 text-[10px] font-medium transition ${b.type === 'url' ? 'border-primary bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted'}`}>
                      ↗ {t('Link')}
                    </button>
                    <button type="button" onClick={() => setBtn(i, { type: 'postback' })}
                      className={`rounded border px-2 py-0.5 text-[10px] font-medium transition ${b.type === 'postback' ? 'border-primary bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted'}`}>
                      💬 {t('Postback')}
                    </button>
                  </div>
                  <div className="flex-1" />
                  {buttons.length > 1 && (
                    <button type="button" onClick={() => removeBtn(i)} className="text-destructive/70 hover:text-destructive" title={t('Remover botão')}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
                <div className="grid gap-2 sm:grid-cols-2">
                  <Input value={b.text} onChange={e => setBtn(i, { text: e.target.value })} placeholder={t('Texto do botão')} maxLength={20} className="h-8 text-xs" />
                  {b.type === 'url' ? (
                    <Input value={b.url} onChange={e => setBtn(i, { url: e.target.value })} placeholder="https://..." className="h-8 text-xs" />
                  ) : (
                    <Input value={b.payload} onChange={e => setBtn(i, { payload: e.target.value })} placeholder={t('palavra-chave (vazio = do texto)')} className="h-8 text-xs" />
                  )}
                </div>
                {b.type === 'postback' && (
                  <div className="rounded border border-primary/20 bg-primary/5 p-2 space-y-1.5">
                    <p className="text-[10px] font-semibold text-primary">💬 {t('Resposta ao clicar')} <span className="font-normal text-muted-foreground">— {t('a pessoa não sai do chat')}</span></p>
                    <textarea value={b.reply_text} onChange={e => setBtn(i, { reply_text: e.target.value })} rows={2}
                      className="w-full rounded border bg-background px-2 py-1 text-xs" placeholder={t('Texto que a pessoa recebe ao tocar no botão')} />
                    <div className="grid gap-1.5 sm:grid-cols-2">
                      <Input value={b.reply_image} onChange={e => setBtn(i, { reply_image: e.target.value })} placeholder={t('URL da imagem (opcional)')} className="h-7 text-[11px]" />
                      <Input value={b.reply_link} onChange={e => setBtn(i, { reply_link: e.target.value })} placeholder={t('Link ao clicar (opcional)')} className="h-7 text-[11px]" />
                    </div>
                    <Input value={b.reply_btn_text} onChange={e => setBtn(i, { reply_btn_text: e.target.value })} placeholder={t('Texto do botão da resposta (ex: Ver agora)')} className="h-7 text-[11px]" />
                    <p className="text-[10px] leading-relaxed text-muted-foreground">
                      {t('Sem resposta preenchida, o botão não responde sozinho. A regra é por página — cadastre a mesma palavra-chave em cada página (keyword flow).')}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">{t('Imagem / GIF (opcional)')}</Label>
            {!imageUrl ? (
              <div className="mt-1">
                <input
                  type="file"
                  accept="image/*,.gif,.webp"
                  disabled={uploadingImg}
                  onChange={async (e) => {
                    const f = e.target.files?.[0]; if (!f) return
                    setUploadingImg(true)
                    try { const r: any = await api.upload(f); setImageUrl(r.url); toast.success(t('Imagem enviada')) }
                    catch (err: any) { toast.error(err?.message || t('Erro no upload')) }
                    finally { setUploadingImg(false) }
                  }}
                  className="block w-full text-xs text-muted-foreground file:mr-2 file:rounded file:border-0 file:bg-muted file:px-2 file:py-1 file:text-xs"
                />
                <p className="mt-1 text-[10px] text-muted-foreground">{t('GIF vira imagem estática (1º frame) — a Meta não aceita GIF animado em template. A imagem é revisada: use algo aceitável.')}</p>
              </div>
            ) : (
              <div className="mt-1 flex items-center gap-2">
                <img src={imageUrl} alt="" className="h-14 w-14 rounded object-cover" />
                <Input value={headerTitle} onChange={e => setHeaderTitle(e.target.value)} placeholder={t('Título do card (topo)')} maxLength={60} className="flex-1" />
                <Button size="sm" variant="ghost" onClick={() => setImageUrl('')}>{t('Remover')}</Button>
              </div>
            )}
          </div>
        </div>

        {/* PASSO 2 — Casca que a Meta revisa */}
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 space-y-2">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-600 dark:text-amber-400">
            <ShieldCheck className="h-3.5 w-3.5" /> {t('Passo 2 · Máscara que a Meta revisa')} <span className="font-normal text-muted-foreground">— {t('mantenha neutra')}</span>
          </div>
          <textarea
            value={shell} onChange={e => setShell(e.target.value)} rows={2}
            className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm"
          />
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[10px] text-muted-foreground"><Wand2 className="mr-0.5 inline h-3 w-3" /> {t('exemplos:')}</span>
            {SHELLS.map((s, i) => (
              <button key={i} type="button" onClick={() => setShell(s)}
                className="rounded border bg-background px-1.5 py-0.5 text-[10px] text-muted-foreground hover:bg-muted">
                {t('máscara')} {i + 1}
              </button>
            ))}
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">{t('Exemplo neutro p/ {{1}} (Meta vê)')}</Label>
              <Input value={example} onChange={e => setExample(e.target.value)} className="mt-1 h-8 text-xs" />
            </div>
            {uses2 && (
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">{t('Exemplo neutro p/ {{2}} (Meta vê)')}</Label>
                <Input value={example2} onChange={e => setExample2(e.target.value)} className="mt-1 h-8 text-xs" />
              </div>
            )}
          </div>
        </div>

        {/* Preview lado a lado */}
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="rounded-lg border border-amber-500/40 p-3">
            <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold text-amber-600 dark:text-amber-400">
              <ShieldCheck className="h-3.5 w-3.5" /> {t('Vai pra aprovação (a Meta revisa)')}
            </div>
            <p className="text-sm">{metaSees || <span className="text-muted-foreground">—</span>}</p>
            {buttons.filter(b => b.text.trim()).map((b, i) => (
              <p key={i} className="mt-1.5 text-[11px] text-primary">{b.type === 'postback' ? '💬' : '↗'} {b.text}</p>
            ))}
          </div>
          <div className="rounded-lg border border-emerald-500/40 p-3">
            <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold text-emerald-600 dark:text-emerald-400">
              <Eye className="h-3.5 w-3.5" /> {t('O lead recebe de verdade')}
            </div>
            <p className="text-sm">{leadGets || <span className="text-muted-foreground">—</span>}</p>
            {buttons.filter(b => b.text.trim()).map((b, i) => (
              <p key={i} className="mt-1.5 truncate text-[11px] text-primary">
                {b.type === 'postback'
                  ? <>💬 {b.text} → {b.reply_text ? t('responde no chat') : t('sem resposta cadastrada')}</>
                  : <>↗ {b.text} → {b.url || '—'}</>}
              </p>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between pt-1">
          <p className="text-[10px] text-muted-foreground">{t('Será criado em todas as suas páginas')} ({accounts.length})</p>
          <Button onClick={handleCreate} disabled={creating} size="sm">
            {creating ? <><Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> {t('Criando...')}</> : <>{t('Criar e enviar p/ revisão')}</>}
          </Button>
        </div>
      </div>

      {/* List */}
      <div className="space-y-2">
        <div className="text-sm font-semibold">{t('Seus templates')}</div>
        {isLoading && <div className="text-xs text-muted-foreground">{t('Carregando...')}</div>}
        {!isLoading && templates.length === 0 && (
          <div className="rounded-lg border border-dashed p-6 text-center text-xs text-muted-foreground">{t('Nenhum template ainda')}</div>
        )}
        {templates.map(tpl => (
          <div key={tpl.id} className="rounded-lg border bg-card p-3">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm font-medium">{tpl.name}</span>
                  <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase">{tpl.category}</span>
                </div>
                <p className="mt-0.5 truncate text-xs text-muted-foreground">{tpl.body}</p>
                {tpl.var1 && <p className="mt-0.5 truncate text-[11px] text-emerald-600 dark:text-emerald-400">👁 {t('lead recebe')}: {fill(tpl.body, tpl.var1, tpl.var2 || '')}</p>}
                {tpl.buttonUrl && <p className="mt-0.5 truncate text-[10px] text-primary">↗ {tpl.buttonText}: {tpl.buttonUrl}</p>}
              </div>
              <div className="flex shrink-0 items-center gap-1.5">
                <Button
                  size="sm" variant="default"
                  disabled={firing === tpl.name || tpl.approved === 0}
                  onClick={() => handleFire(tpl.name)}
                  title={tpl.approved === 0 ? t('Nenhuma página aprovou este template') : t('Disparar')}
                >
                  {firing === tpl.name ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                </Button>
                {onSchedule && (
                  <Button
                    size="sm" variant="outline"
                    disabled={tpl.approved === 0}
                    onClick={() => onSchedule(tpl.name)}
                    title={tpl.approved === 0 ? t('Nenhuma página aprovou este template') : t('Agendar este template')}
                  >
                    <CalendarClock className="h-3.5 w-3.5" />
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => handleDelete(tpl.name)} className="text-destructive">
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
            <div className="mt-2 flex items-center gap-3 text-[11px]">
              <span className="flex items-center gap-1 text-emerald-500"><CheckCircle2 className="h-3 w-3" /> {tpl.approved} {t('aprovadas')}</span>
              {tpl.rejected > 0 && <span className="flex items-center gap-1 text-amber-500"><XCircle className="h-3 w-3" /> {tpl.rejected} {t('rejeitadas')}</span>}
              {tpl.pending > 0 && <span className="flex items-center gap-1 text-muted-foreground"><Clock className="h-3 w-3" /> {tpl.pending} {t('pendentes')}</span>}
              <span className="text-muted-foreground">{t('de')} {tpl.totalPages} {t('páginas')}</span>
            </div>
            {tpl.reasons && Object.keys(tpl.reasons).length > 0 && (
              <p className="mt-1 text-[10px] text-amber-500/80">
                {Object.entries(tpl.reasons).map(([k, v]) => `${k} (${v})`).join(' · ')}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
